import { useMemo, useState } from "react";
import { formatRelativeTime } from "@/lib/format";
import { EVIDENCE_GROUP_LABELS, EVIDENCE_KIND_LABELS } from "@/lib/incidentExecution";
import type { IncidentEvidence } from "@/types";

interface IncidentEvidencePanelProps {
  summary: string;
  evidence: IncidentEvidence[];
  /** Opens Evidence Center and fetches container logs — null when unavailable. */
  onViewLogs?: (() => void) | null;
}

export function IncidentEvidencePanel({
  summary,
  evidence,
  onViewLogs = null,
}: IncidentEvidencePanelProps) {
  const [open, setOpen] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, IncidentEvidence[]>();
    for (const item of evidence) {
      const key = item.kind === "policy_input" ? "policy_input" : item.category || "other";
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return map;
  }, [evidence]);

  const groupTitle = (key: string) => {
    if (key === "policy_input") return "Policy inputs (thresholds & facts evaluated)";
    if (key === "verification") return "Supporting VerificationResults";
    return EVIDENCE_GROUP_LABELS[key] ?? key;
  };

  return (
    <section className="iw-section iw-evidence">
      <h3 className="iw-section__title">Evidence</h3>
      <p className="iw-evidence__summary">{summary}</p>
      <button type="button" className="btn btn-ghost iw-evidence-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? "Hide evidence details" : `▶ Show evidence (${evidence.length})`}
      </button>
      {open ? (
        <div className="iw-evidence-groups">
          {[...grouped.entries()].map(([category, items]) => (
            <div key={category} className="iw-evidence-group">
              <h4 className="iw-evidence-group__title">{groupTitle(category)}</h4>
              <ul className="drawer-list">
                {items.map((item) => (
                  <li key={`${item.kind}-${item.label}-${item.value}`} className="drawer-list__item">
                    <strong>
                      {EVIDENCE_KIND_LABELS[item.kind]
                        ? `${EVIDENCE_KIND_LABELS[item.kind]} · ${item.label}`
                        : item.label}
                    </strong>
                    <span>{item.value}</span>
                    <span className="drawer-list__meta">{formatRelativeTime(item.observedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
      {onViewLogs ? (
        <button type="button" className="btn btn-ghost iw-logs-link" onClick={onViewLogs}>
          View Logs
        </button>
      ) : null}
    </section>
  );
}
